import { Link } from 'react-router'
import { Button } from '@klear/ui/components/button'
import { trpc } from '@klear/ui/lib/trpc'
import { Loader2, RefreshCw, Download, Pencil, Columns3, ArrowLeft } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@klear/ui/components/dropdown-menu'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@klear/ui/components/select'
import { Money } from '@klear/money'
import { KlearDate } from '@klear/date'
import { AgGridReact } from 'ag-grid-react'
import { type ColDef, type GridApi, type GridReadyEvent, type ValueGetterParams } from 'ag-grid-community'
import { useKlearAgGrid } from '@klear/ui/hooks/use-klear-ag-grid'
import { ROUTE_REPORTS } from '@klear/constants'
import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'
import {
  type DateRangeOption,
  type ReportPageSize,
  type ReportViewSelection,
  defaultReportViewSelection,
  downloadCsvText,
  loadHiddenReportColumns,
  loadReportPageSize,
  OrderLinkCell,
  OrderEventLinkCell,
  ReportDateRangeModal,
  reportDataToCsv,
  reportGridColumnOrder,
  reportQueryInput,
  reportViewSummary,
  resolveDateRange
} from '@/app/reports/report-shared'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

const PAYMENTS_ORDER_LINK_TITLES = new Set(['Order ID'])
const PAYMENTS_ORDER_EVENT_LINK_TITLES = new Set(['Order Event ID'])
const HIDDEN_COLS_STORAGE_KEY = 'reports.paymentsHiddenColumns.v1'
const PAGE_SIZE_STORAGE_KEY = 'reports.paymentsPageSize.v1'

export default function PaymentsReport() {
  useDocumentTitle('Payments Report')
  const agGrid = useKlearAgGrid()
  const [viewSelection, setViewSelection] = useState<ReportViewSelection>(() => defaultReportViewSelection())
  const [rangeModalOpen, setRangeModalOpen] = useState(false)
  const [dateRangeOption, setDateRangeOption] = useState<DateRangeOption>('current-month')
  const [customStart, setCustomStart] = useState<KlearDate | undefined>(undefined)
  const [customEnd, setCustomEnd] = useState<KlearDate | undefined>(undefined)
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() =>
    loadHiddenReportColumns(HIDDEN_COLS_STORAGE_KEY)
  )
  const [pageSize, setPageSize] = useState<ReportPageSize>(() => loadReportPageSize(PAGE_SIZE_STORAGE_KEY))
  const [gridApi, setGridApi] = useState<GridApi | null>(null)

  const getPaymentsReportData = trpc.reporting.getPaymentsReportData.useQuery(reportQueryInput(viewSelection), {
    refetchOnWindowFocus: false
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(HIDDEN_COLS_STORAGE_KEY, JSON.stringify([...hiddenColumns]))
    } catch {}
  }, [hiddenColumns])

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, toString(pageSize))
    } catch {}
  }, [pageSize])

  const openRangeModal = () => {
    setDateRangeOption(viewSelection.option)
    if (viewSelection.option === 'date-range') {
      setCustomStart(viewSelection.startDate ? KlearDate.parse(viewSelection.startDate) : undefined)
      setCustomEnd(viewSelection.endDate ? KlearDate.parse(viewSelection.endDate) : undefined)
    }
    setRangeModalOpen(true)
  }

  const rangeModalPrimaryDisabled = dateRangeOption === 'date-range' && (!customStart || !customEnd)

  const applyDateRange = () => {
    const { startDate, endDate } = resolveDateRange(dateRangeOption, customStart, customEnd)
    const startStr = startDate?.toJSON()
    const endStr = endDate?.toJSON()
    if (dateRangeOption !== 'all-time' && (!startStr || !endStr)) return
    setViewSelection({
      option: dateRangeOption,
      startDate: dateRangeOption === 'all-time' ? undefined : startStr,
      endDate: dateRangeOption === 'all-time' ? undefined : endStr
    })
    setRangeModalOpen(false)
  }

  const handleExport = () => {
    const data = getPaymentsReportData.data
    if (!data) return
    const input = reportQueryInput(viewSelection)
    const filename =
      input.startDate && input.endDate ? `payments-${input.startDate}-to-${input.endDate}.csv` : 'payments-report.csv'
    downloadCsvText(reportDataToCsv(data, hiddenColumns, reportGridColumnOrder(gridApi)), filename)
  }

  const toggleColumnVisibility = useCallback((field: string, visible: boolean) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev)
      if (visible) next.delete(field)
      else next.add(field)
      return next
    })
  }, [])

  const gridColumnDefs: ColDef[] = useMemo(() => {
    if (!getPaymentsReportData.data) return []
    return getPaymentsReportData.data.columns.map((col) => {
      const field = toString(col.index)
      const def: ColDef = {
        field,
        headerName: col.title,
        sortable: true,
        resizable: true,
        hide: hiddenColumns.has(field)
      }
      if (PAYMENTS_ORDER_LINK_TITLES.has(col.title)) {
        def.cellRenderer = OrderLinkCell
      }
      if (PAYMENTS_ORDER_EVENT_LINK_TITLES.has(col.title)) {
        def.cellRenderer = OrderEventLinkCell
      }
      if (col.kind === 'date') {
        def.type = 'klearDate'
      } else if (col.kind === 'currency') {
        def.type = ['money', 'numericColumn']
        def.valueGetter = (p: ValueGetterParams<Record<string, string | number | Money | null>>) => {
          const value = p.data?.[field]
          return value instanceof Money ? value : null
        }
      } else {
        def.filter = 'agTextColumnFilter'
      }
      return def
    })
  }, [getPaymentsReportData.data, hiddenColumns])

  const gridRowData = useMemo(() => {
    if (!getPaymentsReportData.data) return []
    const { rows, rowMetas } = getPaymentsReportData.data
    return rows.map((row, rowIdx) => {
      const obj: Record<string, string | number | Money | null> = {
        __rowIdx: rowIdx,
        __currency: rowMetas[rowIdx]?.currency ?? null
      }
      row.forEach((val, colIdx) => {
        obj[toString(colIdx)] = val
      })
      return obj
    })
  }, [getPaymentsReportData.data])

  const summary = reportViewSummary(viewSelection)

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 px-4 py-4">
      <div className="flex shrink-0 items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" asChild>
            <Link to={`/${ROUTE_REPORTS}`}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Reports
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold truncate">Payments report</h1>
        </div>
        <Button
          onClick={handleExport}
          disabled={!getPaymentsReportData.data}
          className="inline-flex items-center shrink-0"
        >
          <Download className="h-4 w-4" />
          <span className="ml-2">Export</span>
        </Button>
      </div>

      {getPaymentsReportData.isError && getPaymentsReportData.error && (
        <p className="text-destructive text-sm shrink-0">{getPaymentsReportData.error.message}</p>
      )}

      <div className="flex shrink-0 items-center justify-between rounded border bg-muted/40 px-3 py-2 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-muted-foreground shrink-0">Viewing:</span>
          <span className="font-medium truncate">{summary}</span>
          {getPaymentsReportData.isFetching && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" />
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Select
            value={toString(pageSize)}
            onValueChange={(v) => setPageSize(v === 'all' ? 'all' : (toNumber(v) as ReportPageSize))}
          >
            <SelectTrigger className="h-8 w-[140px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Show all</SelectItem>
              <SelectItem value="50">50 per page</SelectItem>
              <SelectItem value="100">100 per page</SelectItem>
              <SelectItem value="500">500 per page</SelectItem>
              <SelectItem value="1000">1000 per page</SelectItem>
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" disabled={!getPaymentsReportData.data}>
                <Columns3 className="h-3.5 w-3.5 mr-2" />
                Columns
                {hiddenColumns.size > 0 && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    ({getPaymentsReportData.data ? getPaymentsReportData.data.columns.length - hiddenColumns.size : 0}/
                    {getPaymentsReportData.data?.columns.length ?? 0})
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[60vh] overflow-y-auto">
              <DropdownMenuLabel className="flex items-center justify-between gap-4">
                <span>Toggle columns</span>
                <button
                  type="button"
                  onClick={() => setHiddenColumns(new Set())}
                  className="text-xs font-normal text-muted-foreground hover:text-foreground"
                  disabled={hiddenColumns.size === 0}
                >
                  Show all
                </button>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {getPaymentsReportData.data?.columns.map((col) => {
                const field = toString(col.index)
                const visible = !hiddenColumns.has(field)
                return (
                  <DropdownMenuCheckboxItem
                    key={field}
                    checked={visible}
                    onCheckedChange={(next) => toggleColumnVisibility(field, Boolean(next))}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {col.title}
                  </DropdownMenuCheckboxItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" variant="ghost" onClick={openRangeModal}>
            <Pencil className="h-3.5 w-3.5 mr-2" />
            Change date range
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => getPaymentsReportData.refetch()}
            disabled={getPaymentsReportData.isFetching}
          >
            {getPaymentsReportData.isFetching ? (
              <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col rounded-md border">
        {getPaymentsReportData.isFetching && !getPaymentsReportData.data ? (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading payments…
          </div>
        ) : (
          <div className="h-full min-h-0 w-full">
            <AgGridReact
              rowHeight={32}
              headerHeight={36}
              rowData={gridRowData}
              columnDefs={gridColumnDefs}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              animateRows={false}
              pagination={pageSize !== 'all'}
              paginationPageSize={pageSize === 'all' ? undefined : pageSize}
              paginationPageSizeSelector={false}
              theme={agGrid.theme}
              columnTypes={agGrid.columnTypes}
              enableCellTextSelection={true}
              onGridReady={(event: GridReadyEvent) => setGridApi(event.api)}
            />
          </div>
        )}
      </div>

      <ReportDateRangeModal
        open={rangeModalOpen}
        onOpenChange={setRangeModalOpen}
        title="Change date range"
        primaryText="Apply"
        idPrefix="payments"
        dateRangeOption={dateRangeOption}
        onDateRangeOptionChange={setDateRangeOption}
        customStart={customStart}
        customEnd={customEnd}
        onCustomStartChange={setCustomStart}
        onCustomEndChange={setCustomEnd}
        onConfirm={applyDateRange}
        primaryDisabled={rangeModalPrimaryDisabled}
      />
    </div>
  )
}
